/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getCatalogId, getUrlSync } from '@msdyn365-commerce/core';
import { ProductPrice, ProductRefiner, ProductRefinerSource, ProductSearchResult, ProductsDataActions, ProjectionDomain, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions, Random, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { createSearchStateInput } from '@msdyn365-commerce-modules/search-utilities';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import * as React from 'react';

import getSearchSuggestionsAction, { AutoSuggestInput, SuggestionType } from './actions/get-auto-suggest-suggestions';
import {
    CategorySuggestionsComponent,
    FormComponent,
    ISearchCategoryViewProps,
    ISearchKeywordViewProps,
    ISearchProductViewProps,
    KeywordSuggestionsComponent,
    ProductSuggestionsComponent
} from './components';
import { IDlvrySearchData } from './dlvry-search.data';
import { IDlvrySearchProps, ISuggestionTypeCriterionData, SuggestionTypeCriterionSuggestionType } from './dlvry-search.props.autogenerated';
import { getActivePricesAsync, getCategoryPathsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import PQueue from 'p-queue';
import { BuyValidationResult } from '../../themes/dlvry-adventureworks/views/components/product.component';
import { onBuyHasAccessValidator } from '../../shared-utils/attributes/on-buy-access-validator';
import BarcodeScanner from '../../components/barcode-scanner/barcode-scanner';
import { ProductSearchResultExt } from '../../shared-utils/types';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { applyAttributesToProduct } from '../../shared-utils/attributes';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';

export interface ISearchState {
    searchText: string;
    searchKeywordSuggestClass: string;
    searchProductSuggestClass: string;
    searchCategorySuggestClass: string;
    suggestions: Autosuggestions | undefined;
    searchRefiners?: ProductRefiner[] | undefined;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoadingAutoSuggest?: boolean;
    searchTermCompleted?: string;
    activePrices: { [key: string]: ProductPrice };
    simpleProducts: SimpleProduct[];
    productSearchResults: ProductSearchResult[];
    showScannerModal: boolean;
    unitConversions: UnitOfMeasureConversion[];
    quantities: Map<number, number>;
    breakoffs: Map<number, boolean>;
}

export interface ISearchViewProps extends IDlvrySearchProps<IDlvrySearchData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    AutoSuggest: INodeProps;
    KeywordSuggest: INodeProps;
    ProductSuggest: INodeProps;
    CategorySuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    UlKeyword: INodeProps;
    UlProduct: INodeProps;
    UlCategory: INodeProps;
    form: React.ReactNode;
    autosuggestKeyword?: ISearchKeywordViewProps;
    autosuggestProduct?: ISearchProductViewProps;
    autosuggestCategory?: ISearchCategoryViewProps;
    callbacks: {
        handleCancelSearchChange(): void;
        handleCancelSearchFocused?(): void;
    };
    isLoadingAutoSuggest?: boolean;
    isLoadingNode?: React.ReactNode;
    searchTermCompleted?: string;
    scannerModal?: React.ReactNode;
}

/**
 *
 * Search module.
 * @extends {React.Component<IDlvrySearchProps<IDlvrySearchData>, ISearchState>}
 */
class Search extends React.Component<IDlvrySearchProps<IDlvrySearchData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly topResultsCount?: number;

    private readonly hitPrefix: string = '{';

    private readonly hitSuffix: string = '}';

    private readonly waitTime: number = 500;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    private readonly telemetryContent: ITelemetryContent;

    private loadSuggestionsRequestId?: string;

    private inputSuggestionType: ISuggestionTypeCriterionData[];

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    private cartQueue = new PQueue({ concurrency: 1 });

    public constructor(props: IDlvrySearchProps<IDlvrySearchData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();
        this.state = {
            searchText: '',
            searchKeywordSuggestClass: 'ms-search__autoSuggest__keyword',
            searchProductSuggestClass: 'ms-search__autoSuggest__product',
            searchCategorySuggestClass: 'ms-search__autoSuggest__category',
            suggestions: undefined,
            searchRefiners: undefined,
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoadingAutoSuggest: false,
            activePrices: {},
            simpleProducts: [],
            productSearchResults: [],
            showScannerModal: false,
            unitConversions: [],
            quantities: new Map<number, number>(),
            breakoffs: new Map<number, boolean>()
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            50;

        this.inputSuggestionType = [];
        this.topResultsCount = this.props.config && this.props.config.topResultsCount ? this.props.config.topResultsCount : 5;
        this.searchFormClass = 'ms-search__form';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this._initSuggestionTypes();
    }

    public async componentDidMount(): Promise<void> {
        document.body && document.body.addEventListener('mousedown', this._focusOutTarget);
        window && window.addEventListener('keyup', this._keyup);
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._focusOutTarget, false);
        window && window.removeEventListener('keyup', this._keyup, false);
    }

    public componentDidUpdate(): void {
        void this._getActivePrices();
        void this._getSimpleProducts();
        void this._getUnitConversions();
    }

    public shouldComponentUpdate(nextProps: IDlvrySearchProps<IDlvrySearchData>, nextState: ISearchState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: { cancelBtnAriaLabel, searchBtnAriaLabel, submitBtnAriaLabel },
            config: { disableSubmitSearch, searchplaceholderText, shouldShowFullCategoryPath, className = '' }
        } = this.props;

        const {
            searchText,
            searchKeywordSuggestClass,
            searchProductSuggestClass,
            searchCategorySuggestClass,
            suggestions,
            searchRefiners,
            isLoadingAutoSuggest
        } = this.state;

        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        const searchURL = this._appendQueryParams(searchPageURL, searchText, searchQsp);
        const searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : 'Search in Fabrikam';

        const keywordSuggestions = suggestions?.AllSearchResults?.filter(
            suggestion => suggestion.SuggestionType === SuggestionType.Keyword || suggestion.SuggestionType === SuggestionType.None
        );
        const productSuggestions = suggestions?.AllSearchResults?.filter(
            suggestion => suggestion.SuggestionType === SuggestionType.Product
        );
        const categoryRefiners = searchRefiners?.find(refiner => {
            return refiner.SourceValue === ProductRefinerSource.Category;
        });
        const categorySuggestions = suggestions?.AllSearchResults?.filter(suggestion => {
            if (suggestion.SuggestionType !== SuggestionType.Category) {
                return false;
            }
            const refiner = categoryRefiners?.Values?.find(item => {
                return `${item.RefinerRecordId ?? 0}` === suggestion.Id;
            });

            return !ObjectExtensions.isNullOrUndefined(refiner);
        });

        const viewProps: ISearchViewProps = {
            ...(this.props as IDlvrySearchProps<IDlvrySearchData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: classnames('ms-search__autoSuggest', { show: this.state.searchTermCompleted, hide: !this.state.searchTermCompleted })
            },
            KeywordSuggest: {
                className: searchKeywordSuggestClass
            },
            ProductSuggest: {
                className: searchProductSuggestClass
            },
            CategorySuggest: {
                className: searchCategorySuggestClass
            },
            SearchForm: {
                className: `${this.searchFormClass}`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off',
                onSubmit: this._handleSubmit
            },
            UlKeyword: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__keywordResults-items', className)
            },
            UlProduct: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__productResults-items', className)
            },
            UlCategory: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__categoryResults-items', className)
            },
            form: FormComponent({
                context: this.props.context,
                ariaLabelCancel: cancelBtnAriaLabel,
                ariaLabelSearch: searchBtnAriaLabel,
                ariaLabelSubmit: submitBtnAriaLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                handleInputChange: this._handleInputChange,
                handleCancelSearchChange: this._handleCancelSearchChange,
                handleScannerButtonClick: this._handleScannerButtonClick,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText
            }),
            autosuggestKeyword:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (keywordSuggestions && ArrayExtensions.hasElements(keywordSuggestions)))
                    ? KeywordSuggestionsComponent({
                          searchKeywordSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          searchTermCompleted: this.state.searchTermCompleted,
                          suggestions: keywordSuggestions,
                          telemetryContent: this.telemetryContent
                      })
                    : undefined,
            autosuggestProduct:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (productSuggestions && ArrayExtensions.hasElements(productSuggestions)))
                    ? ProductSuggestionsComponent({
                          searchProductSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: productSuggestions,
                          telemetry: this.props.telemetry,
                          moduleType: this.props.typeName,
                          moduleId: this.props.id,
                          telemetryContent: this.telemetryContent,
                          locale: this.props.context.request.locale,
                          resources: this.props.resources,
                          activePrices: this.state.activePrices,
                          simpleProducts: this.state.simpleProducts,
                          cart: this.props.data.cart.result,
                          cartQueue: this.cartQueue,
                          unitConversions: this.state.unitConversions,
                          quantities: this.state.quantities,
                          breakoffs: this.state.breakoffs,
                          onQuantityChange: this._onQuantityChange,
                          onBreakoffChange: this._onBreakoffChange,
                          onBuyValidate: this._onBuyValidate
                      })
                    : undefined,
            autosuggestCategory:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (categorySuggestions && ArrayExtensions.hasElements(categorySuggestions)))
                    ? CategorySuggestionsComponent({
                          searchCategorySuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: categorySuggestions,
                          searchRefiners,
                          telemetryContent: this.telemetryContent,
                          shouldShowFullPath: shouldShowFullCategoryPath,
                          searchTermCompleted: this.state.searchTermCompleted,
                          categorySuggestionProductCount: this.props.resources.categorySuggestionProductCount,
                          categorySuggestionProductCountPlural: this.props.resources.categorySuggestionProductCountPlural,
                          categorySuggestionJoinerText: this.props.resources.categorySuggestionJoinerText
                      })
                    : undefined,
            callbacks: {
                handleCancelSearchChange: this._handleCancelSearchChange,
                handleCancelSearchFocused: this._handleCancelSearchFocused
            },
            isLoadingAutoSuggest,
            isLoadingNode: this._isLoadingNode(),
            searchTermCompleted: this.state.searchTermCompleted,
            scannerModal: this._renderScannerModal()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _initSuggestionTypes() {
        const suggestionTypes = this.props.config.suggestionTypeCriterion;

        const suggestions = ArrayExtensions.unique(ArrayExtensions.validValues(suggestionTypes));
        if (!ArrayExtensions.hasElements(suggestions)) {
            this.inputSuggestionType = [
                { SuggestionType: SuggestionTypeCriterionSuggestionType.keyword },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.product },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.scopedCategory }
            ];
            return;
        }

        const validSuggestions = suggestions.filter(suggestion => suggestion.SuggestionType);
        if (ArrayExtensions.hasElements(validSuggestions)) {
            this.inputSuggestionType = validSuggestions;
            return;
        }

        this.inputSuggestionType = [];
    }

    /**
     * Function to create the ReactNode for loading Autosuggest result.
     * @returns ReachNode for loading Autosuggest result.
     */
    private readonly _isLoadingNode = (): React.ReactNode => {
        const loadingText = this.props.resources.autoSuggestResultLoadingMessage;
        return <Spinner className='msc-autoSuggest__loadingResult' label={loadingText} size={SpinnerSize.medium} labelPosition='right' />;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _focusOutTarget = (e: any): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.target.closest('.ms-search') === null) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _keyup = (e: KeyboardEvent): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.keyCode === KeyCodes.Escape) {
            this._clearAutoSuggestState();
        }

        if (
            !(
                (this.formReference.current && this.formReference.current.contains((e.target as unknown) as Node)) ||
                (this.autoSuggestResultDiv.current && this.autoSuggestResultDiv.current.contains((e.target as unknown) as Node))
            )
        ) {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }
        }
    };

    private readonly _clearAutoSuggestState = (): void => {
        const {
            context: { actionContext },
            data: { searchState }
        } = this.props;

        if (this.searchTextInput.current && this.searchTextInput.current.value.length > 0) {
            searchState.searchText = '';
            searchState.defaultBasicSearchUrl = '';
            searchState.searchQueryStringParameter = '';
            actionContext ? actionContext.update(createSearchStateInput(searchState), searchState) : null;
            this.searchTextInput.current.value = '';
        }

        this.setState({ searchText: '', suggestions: undefined, searchRefiners: undefined, searchTermCompleted: '' });
    };

    private async _getActivePrices() {
        const { context } = this.props;

        if (!context.request.user.isAuthenticated) {
            return;
        }

        const missingPrices = this.state.suggestions?.AllSearchResults?.filter(x => x.SuggestionType === 'Product' && !this.state.activePrices[x.Id!]);
        const projectDomain: ProjectionDomain = { ChannelId: +context.request.apiSettings.channelId, CatalogId: context.request.apiSettings.catalogId };

        if (!missingPrices || missingPrices.length === 0) {
            return;
        }

        const prices = await getActivePricesAsync({ callerContext: context.actionContext },
            projectDomain,
            missingPrices.map(x => +x.Id!),
            new Date(),
            null,
            undefined,
            true);

        const newActivePrices = { ...this.state.activePrices };
        prices.forEach(x => {
            if (x.ProductId && x.CustomerContextualPrice) {
                newActivePrices[x.ProductId.toString()] = x;
            }
        });

        this.setState({ activePrices: newActivePrices });
    }

    private async _getSimpleProducts() {
        const { context } = this.props;
        const missingProducts = this.state.suggestions?.AllSearchResults
            ?.filter(x => x.Id && x.SuggestionType === 'Product' && !this.state.simpleProducts.find(y => y.RecordId === parseInt(x.Id!, 10)));

        if (!missingProducts || missingProducts.length === 0) {
            return;
        }

        const ids = missingProducts.filter(x => x.Id).map(x => parseInt(x.Id!, 10));
        const p1 = ProductsDataActions.getByIdsAsync(
            { callerContext: context.actionContext },
            context.request.apiSettings.channelId,
            ids
        );
        const p2 = searchByCriteriaAsync({ callerContext: context.actionContext }, {
            Context: {
                CatalogId: context.request.apiSettings.catalogId,
                ChannelId: context.request.apiSettings.channelId
            },
            Ids: ids,
            IncludeAttributes: true
        });
        const p3 = getCategoryPathsAsync({ callerContext: context.actionContext },
            context.request.apiSettings.channelId,
            context.request.apiSettings.catalogId,
            ids.map(productId => ({ ProductId: productId }))
        );

        const [simpleProducts, searchResults, categoryPaths] = await Promise.all([p1, p2, p3]);

        for (const product of simpleProducts) {
            applyAttributesToProduct(context.request,
                product,
                searchResults.find(sr => sr.RecordId === product.RecordId)?.AttributeValues,
                categoryPaths.find(cp => cp.ProductId === product.RecordId)?.CategoryPath
            );
        }

        const newSimpleProducts = [ ...this.state.simpleProducts, ...simpleProducts ];

        this.setState({ simpleProducts: newSimpleProducts });
    }

    private async _getUnitConversions() {
        const { context } = this.props;
        const missingProducts = this.state.suggestions?.AllSearchResults
            ?.filter(x => x.Id && x.SuggestionType === 'Product' && !this.state.unitConversions.find(y => y.ProductRecordId === parseInt(x.Id!, 10)));

        if (!missingProducts || missingProducts.length === 0) {
            return;
        }

        const unitConversions = await getProductUnitOfMeasureOptionsAsync(
            { callerContext: context.actionContext },
            missingProducts.map(x => parseInt(x.Id!, 10))
        );

        // Add a placeholder for products that don't have any unit conversions so this doesn't infinitely loop
        missingProducts.forEach(x => {
            const productId = parseInt(x.Id!, 10);
            const productUnitConversions = unitConversions.filter(y => y.ProductRecordId === productId);
            if (productUnitConversions.length === 0) {
                unitConversions.push({ ProductRecordId: productId });
            }
        });

        const newUnitConversions = [ ...this.state.unitConversions, ...unitConversions ];
        this.setState({ unitConversions: newUnitConversions });
    }

    private async _getSearchSuggestions(loadSuggestionsRequestId: string, query: string) {
        try {
            const searchSuggestions = await getSearchSuggestionsAction(
                new AutoSuggestInput(query, this.topResultsCount, this._getSuggestionType(), this.hitPrefix, this.hitSuffix),
                this.props.context.actionContext
            );
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return undefined;
            }
            return searchSuggestions;
        } catch (error) {
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return undefined;
            }
            this.setState({ suggestions: undefined, searchRefiners: undefined, autoSuggestAriaLabel: this._setAutoSuggestResultCount() });
            this.props.telemetry.exception(error);
            this.props.telemetry.debug('Unable to get auto suggest results');
            return undefined;
        }
    }

    private async _updateSearchRefiners(loadSuggestionsRequestId: string, query: string, suggestions: Autosuggestions | undefined) {
        try {
            const refiners = await this._getSearchRefiners(query);
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return;
            }
            this.setState({
                suggestions,
                searchRefiners: refiners,
                autoSuggestAriaLabel: this._setAutoSuggestResultCount(suggestions),
                isLoadingAutoSuggest: false,
                searchTermCompleted: query
            });
        } catch (error) {
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return;
            }
            this.setState({ suggestions, searchRefiners: undefined, autoSuggestAriaLabel: this._setAutoSuggestResultCount(suggestions) });
            this.props.telemetry.exception(error);
            this.props.telemetry.debug('Unable to get refiners results');
        }
    }

    private _loadSuggestions(query: string) {
        this.cancellabelDebounce = debounce(async () => {
            this.setState({ isLoadingAutoSuggest: true });
            const loadSuggestionsRequestId = Random.Guid.generateGuid();
            this.loadSuggestionsRequestId = loadSuggestionsRequestId;
            const searchSuggestions = await this._getSearchSuggestions(loadSuggestionsRequestId, query);
            void GTM.searchEventAsync(query);
            await this._updateSearchRefiners(loadSuggestionsRequestId, query, searchSuggestions);
        }, this.waitTime);

        setTimeout(() => {
            this.cancellabelDebounce();
        }, 0);
    }

    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length <= this.maxChars) {
            this.setState({ searchText: query });
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }

        const {
            context,
            data: { searchState }
        } = this.props;

        if (context) {
            if (query && query.length > 0) {
                if (query.length > this.maxChars) {
                    return;
                }

                if (ArrayExtensions.hasElements(this.inputSuggestionType)) {
                    this._loadSuggestions(query);
                }

                searchState.searchText = query;
            } else {
                this.setState({ suggestions: undefined, searchRefiners: undefined });
                searchState.searchText = '';
            }
            context.actionContext.update(createSearchStateInput(searchState), searchState);
        }
    };

    private _getSuggestionType(): string {
        const distinctSuggestionTypes: string[] = this.inputSuggestionType.map(
            suggestionTypeItem => suggestionTypeItem.SuggestionType!.charAt(0).toUpperCase() + suggestionTypeItem.SuggestionType!.slice(1)
        );
        return distinctSuggestionTypes.join(',');
    }

    private async _getSearchRefiners(searchText: string): Promise<ProductRefiner[]> {
        const { actionContext: ctx } = this.props.context;
        const catalogId = getCatalogId(ctx.requestContext);
        validateCatalogId(catalogId);
        return ProductsDataActions.getProductSearchRefinersAsync(
            { callerContext: ctx },
            {
                SearchCondition: searchText,
                Context: {
                    ChannelId: +ctx.requestContext.apiSettings.channelId,
                    CatalogId: catalogId
                }
            }
        );
    }

    private readonly _handleSubmit = (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement> | undefined
    ): void => {
        const {
            config: { disableSubmitSearch },
            context: {
                actionContext,
                app: {
                    config: { searchQueryStringParameter }
                }
            }
        } = this.props;

        const query = this.searchTextInput.current && this.searchTextInput.current.value;
        e?.preventDefault();

        if (!query || (query && query.length === 0) || disableSubmitSearch) {
            this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
        } else {
            const searchQsp = searchQueryStringParameter || 'q';
            const searchURL = this._appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
            document.location.href = searchURL;
        }
    };

    private readonly _handleCancelSearchChange = (): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults) {
            this._clearAutoSuggestState();
        }

        // This might be redundant if the check above always returns true
        this.setState({
            searchText: '',
            isInitialState: false,
            searchTermCompleted: ''
        });
    };

    private readonly _handleScannerButtonClick = (): void => {
        this.setState({ showScannerModal: true });
    };

    private readonly _handleCancelSearchFocused = (): void => {
        const { suggestions } = this.state;
        if (!(suggestions && suggestions.AllSearchResults)) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }

        return queryUrl.href;
    };

    private readonly _setAutoSuggestResultCount = (result?: Autosuggestions) => {
        if (this.autoSuggestResultScreenReaderDiv.current !== null) {
            const ariaLiveRegion = this.autoSuggestResultScreenReaderDiv.current;
            const suggestResultString = this.props.resources.autoSuggestFoundMessage;
            const emptyResultString = this.props.resources.noAutoSuggestionMessage;
            ariaLiveRegion.innerText =
                result && result.AllSearchResults && result.AllSearchResults.length > 0 ? suggestResultString : emptyResultString;
            ariaLiveRegion.setAttribute('aria-live', 'assertive');
            return ariaLiveRegion.innerText;
        }
        return '';
    };

    private readonly _onQuantityChange = (productId: number, quantity: number): void => {
        this.setState(prevState => {
            const newQuantities = new Map(prevState.quantities);
            newQuantities.set(productId, quantity);
            return { quantities: newQuantities };
        });
    };

    private readonly _onBreakoffChange = (productId: number, breakoff: boolean): void => {
        this.setState(prevState => {
            const newBreakoffs = new Map(prevState.breakoffs);
            newBreakoffs.set(productId, breakoff);
            return { breakoffs: newBreakoffs };
        });
    };

    private readonly _onBuyValidate = async (product: ProductSearchResultExt, quantity: number): Promise<BuyValidationResult> => {
        const { context, data, resources } = this.props;

        // Buy validation happens on attributes, so we need to get the full product details.
        // Check if we already have the product details in the state and if not fetch them. Fetches all products at the same time to reduce the time needed instead of doing one by one.
        const missingProducts = this.state.suggestions?.AllSearchResults
            ?.filter(x => x.Id && x.SuggestionType === 'Product' && !this.state.productSearchResults.find(y => y.RecordId === parseInt(x.Id!, 10)));

        if (missingProducts && missingProducts.length > 0) {
            // Get full product details including attributes for the missing products
            const searchResults = await searchByCriteriaAsync(
                { callerContext: context.actionContext },
                {
                    Context: {
                        CatalogId: getCatalogId(context.actionContext.requestContext),
                        ChannelId: +context.actionContext.requestContext.apiSettings.channelId
                    },
                    Ids: missingProducts.map(x => parseInt(x.Id!, 10)),
                    IncludeAttributes: true
                });

            this.setState({ productSearchResults: [...this.state.productSearchResults, ...searchResults] });
        }

        return onBuyHasAccessValidator(
            context,
            data.orgInformation.result,
            this.state.productSearchResults.find(x => x.RecordId === product.RecordId) as ProductSearchResultExt,
            resources.toastNoAccessToProduct
        );
    };

    private readonly _renderScannerModal = (): React.ReactNode => {
        if (!this.props.config.enableBarcodeScanner) {
            return null;
        }

        return (
            <BarcodeScanner
                context={this.props.context}
                modalIsOpen={this.state.showScannerModal}
                onClose={() => this.setState({ showScannerModal: false })}
                onFound={(text: string) => {
                    this._handleInputChange(text);
                    this._handleSubmit(undefined);
                    this.setState({ showScannerModal: false });
                }}
                resources={{
                    noCameraDevicesFound: this.props.resources.noCameraDevicesFoundText,
                    errorGettingDevices: this.props.resources.errorGettingDevicesText,
                    loading: this.props.resources.autoSuggestResultLoadingMessage,
                    chooseCamera: this.props.resources.chooseCameraText,
                    changeCamera: this.props.resources.changeCameraText,
                    close: this.props.resources.modalCloseButtonText
                }}
            />
        );
    };
}

export default Search;
